import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, Check } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Hint } from '@/components/Hint';
import { Can } from '@/auth/guards';
import {
  listFetchDomains, setFetchDomainStatus, type FetchDomain,
} from '@/api/fetchDomains';
import { JourneyName, QUEUE_CARD_CLASS, RowAction } from '@/features/orders/workLib';
import { fmtDate } from '@/features/orders/lib';
import { cn } from '@/lib/utils';
import { PageState } from './PageState';

type StatusFilter = 'allowed' | 'blocked' | 'all';

/**
 * The domains the server fetches vendor documents from (plan W3). Public
 * domains join on first use; Block stops fetches at once. Reached from the
 * account menu.
 */
export function FetchDomainsPage() {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<FetchDomain[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('allowed');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDomains(await listFetchDomains());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load fetch domains');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => ({
    allowed: domains?.filter((d) => d.status === 'allowed').length ?? 0,
    blocked: domains?.filter((d) => d.status === 'blocked').length ?? 0,
  }), [domains]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (domains ?? [])
      .filter((d) => status === 'all' || d.status === status)
      .filter((d) => !q || d.domain.includes(q) || (d.vendor ?? '').toLowerCase().includes(q));
  }, [domains, status, search]);

  const act = async (domain: string, fn: () => Promise<void>, done: string) => {
    setBusy(domain);
    try {
      await fn();
      toast.success(done);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update the domain');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <PageHeader
        title="Fetch domains"
        about="Sites the server may download vendor documents from. A public site is added the first time a vendor link uses it."
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6 max-md:px-3 max-md:pb-4">
        {!domains ? (
          <PageState loading={!error} error={error} onRetry={load} />
        ) : (
          <div className={QUEUE_CARD_CLASS}>
            <div className="flex flex-wrap items-center gap-2 border-b border-border py-3">
              <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
                <SelectTrigger aria-label="Status" className="w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="allowed">Allowed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Input
                id="fetch-domain-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search domains or vendors"
                className="h-8 w-56 text-xs max-md:w-full"
              />
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                {counts.allowed} allowed · {counts.blocked} blocked
              </span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>First used for</TableHead>
                    <TableHead className="text-right">Added</TableHead>
                    <TableHead className="text-right">Last used</TableHead>
                    <TableHead className="text-right">Uses</TableHead>
                    <TableHead><span className="sr-only">Actions</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No domains match.</TableCell></TableRow>
                  )}
                  {rows.map((d) => (
                    <TableRow key={d.domain}>
                      <TableCell className="font-mono text-[12.5px]">
                        {d.firstUrl ? <Hint content={d.firstUrl}><span>{d.domain}</span></Hint> : d.domain}
                      </TableCell>
                      <TableCell className="min-w-0 max-w-[28rem] whitespace-normal">
                        {d.orderId && d.vendor ? (
                          <JourneyName vendor={d.vendor} label={d.label} onOpen={() => navigate(`/journeys/${encodeURIComponent(d.orderId!)}`)} />
                        ) : d.setupFolder ? (
                          <span className="text-sm">{d.setupFolder}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtDate(d.addedAt)}</TableCell>
                      <TableCell className={cn('text-right tabular-nums', !d.lastUsedAt && 'text-muted-foreground')}>
                        {d.lastUsedAt ? fmtDate(d.lastUsedAt) : 'never'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{d.useCount}</TableCell>
                      <TableCell>
                        {/* PUT /fetch-domains is orders:write:documents. */}
                        <Can code="orders:write:documents">
                          <div className="flex justify-end gap-1">
                            {d.status === 'allowed' ? (
                              <RowAction
                                variant="danger"
                                icon={<Ban className="h-3 w-3" />}
                                disabled={busy === d.domain}
                                onClick={() => act(d.domain, () => setFetchDomainStatus(d.domain, 'blocked'), `${d.domain} blocked`)}
                              >
                                Block
                              </RowAction>
                            ) : (
                              <RowAction
                                variant="quiet"
                                icon={<Check className="h-3 w-3" />}
                                disabled={busy === d.domain}
                                onClick={() => act(d.domain, () => setFetchDomainStatus(d.domain, 'allowed'), `${d.domain} allowed`)}
                              >
                                Allow
                              </RowAction>
                            )}
                          </div>
                        </Can>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
